import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, inject } from '@angular/core';
import { BehaviorSubject, Subject, combineLatest, distinctUntilChanged, map, takeUntil } from 'rxjs';

import { TenantBrandingService, tenantBrandingDefaults } from '../../tenant/tenant-branding.service';
import { TenantPublicContentService } from '../../tenant/tenant-public-content.service';

const LEGACY_ASSET_PATTERN = /^\/assets\/[a-z0-9._/-]+$/i;

@Component({
  selector: 'app-tenant-hero-image',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './tenant-hero-image.component.html',
  styleUrls: ['./tenant-hero-image.component.css'],
})
export class TenantHeroImageComponent implements OnDestroy {
  private readonly branding = inject(TenantBrandingService);
  private readonly tenantContent = inject(TenantPublicContentService);
  private readonly destroy$ = new Subject<void>();
  private readonly legacyImageUrlSubject = new BehaviorSubject<string>(tenantBrandingDefaults.heroImageUrl);
  private readonly renderedSourceSubject = new BehaviorSubject<string>(tenantBrandingDefaults.heroImageUrl);

  private requestedSource = tenantBrandingDefaults.heroImageUrl;
  private fallbackAttempted = false;

  readonly renderedSource$ = this.renderedSourceSubject.asObservable();
  imageHidden = false;

  @Input({ required: true })
  set worldLeagueImageUrl(value: string) {
    const normalized = String(value || '').trim();
    this.legacyImageUrlSubject.next(
      LEGACY_ASSET_PATTERN.test(normalized) ? normalized : tenantBrandingDefaults.heroImageUrl,
    );
  }

  constructor() {
    combineLatest([
      this.branding.branding$,
      this.tenantContent.content$,
      this.legacyImageUrlSubject,
    ]).pipe(
      map(([branding, content, legacyImageUrl]) =>
        content.isWorldLeagueTenant === true ? legacyImageUrl : branding.heroImageUrl,
      ),
      distinctUntilChanged(),
      takeUntil(this.destroy$),
    ).subscribe((source) => {
      this.requestedSource = source;
      this.fallbackAttempted = false;
      this.imageHidden = false;
      this.renderedSourceSubject.next(source);
    });
  }

  onImageError(): void {
    if (this.imageHidden) return;

    if (!this.fallbackAttempted && this.requestedSource !== tenantBrandingDefaults.heroImageUrl) {
      this.fallbackAttempted = true;
      this.renderedSourceSubject.next(tenantBrandingDefaults.heroImageUrl);
      return;
    }

    this.imageHidden = true;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
